import type { BroadcastWarning } from "@bundle/core";
import { Button, Sheet, Stack, Text } from "../primitives/index.ts";
import { useT } from "../i18n/I18nProvider.tsx";

export type BroadcastConfirmDialogProps = {
  /** ChannelViewModel `broadcastConfirm`; absent = closed. */
  confirm: BroadcastWarning | undefined;
  onConfirm: () => void;
  onCancel: () => void;
  testID: string;
};

/**
 * Slack's "Are you sure?" before @channel / @everyone / @here notifies more than 6 people: how many people it reaches and
 * in how many time zones, Cancel (the draft stays) / Send anyway.
 */
export function BroadcastConfirmDialog({ confirm, onConfirm, onCancel, testID }: BroadcastConfirmDialogProps) {
  const t = useT();
  return (
    <Sheet
      open={confirm !== undefined}
      onClose={onCancel}
      title={confirm === undefined ? "" : t("broadcast.confirm.title", { count: confirm.people })}
      detents={["medium"]}
      closeLabel={t("common.close")}
      testID={testID}
    >
      {confirm === undefined ? null : (
        <Stack gap="4">
          <Stack gap="2">
            <Text color="text.secondary" testID={`${testID}-body`}>
              {confirm.range === "here" ? t("broadcast.confirm.here") : t("broadcast.confirm.channel", { range: `@${confirm.range}` })}
            </Text>
            {confirm.timeZones === undefined || confirm.timeZones < 2 ? null : (
              <Text color="text.secondary" testID={`${testID}-zones`}>
                {t("broadcast.confirm.zones", { count: confirm.timeZones })}
              </Text>
            )}
          </Stack>
          <Stack direction="horizontal" gap="2" justify="end">
            <Button label={t("common.cancel")} variant="secondary" onPress={onCancel} testID={`${testID}-cancel`} />
            <Button label={t("broadcast.confirm.send")} variant="primary" onPress={onConfirm} testID={`${testID}-confirm`} />
          </Stack>
        </Stack>
      )}
    </Sheet>
  );
}
